/**
 * promo-price-override.js
 *
 * Applies the active promo discount (from window.__promoConfig) to price elements
 * that are NOT already handled by Liquid or dedicated JS:
 *
 *   - buy-box-form-v2 (new buy box)         — handled in buy-box-form-v2.liquid / .js
 *   - core-react-product-form subscription  — handled via numeric price in json-selling-plan-allocation.liquid
 *   - core-react-product-form OTP option    — patched here via DOM (variant.price not patchable in Liquid)
 *
 * Tiered promos (display only): when __promoConfig.tier2Percent > 0, the applied
 * percent follows the CART contents — base percent below tier2MinItems eligible
 * items, tier2Percent at or above it (GWP gift lines never count). Checkout money
 * comes from Shopify automatic discounts configured to match, so this layer only
 * keeps what the shopper SEES in sync with what they'll pay. On tier changes we
 * re-patch every priced element (originals are kept in data attributes) and emit
 * `promo:tier-change` so the buy-box components can re-render too.
 */
(function () {
  'use strict';

  const config = /** @type {{ enabled?: boolean, discountPercent?: number, bundlePercent?: number, pageProductPercent?: number, tier2Percent?: number, tier2MinItems?: number } | undefined} */ (window.__promoConfig);
  if (!config || !config.enabled || !Number(config.discountPercent)) return;

  // Per-product-type promos: on product pages promo-config resolves the percent for
  // THIS product (bundle PDPs get the bundle percent), so the legacy React buy box
  // patches with the right value. Falls back to the sitewide base percent.
  const BASE_PERCENT = Number(config.pageProductPercent || 0) > 0
    ? Number(config.pageProductPercent)
    : Number(config.discountPercent);
  const TIER2_PERCENT = Number(config.tier2Percent || 0);
  const TIER2_MIN_ITEMS = Math.max(2, Number(config.tier2MinItems || 2));

  // Same GWP line-item properties the cart drawer uses to mark gift lines.
  const GWP_THRESHOLD_KEY_PROPERTY = '_gwp_threshold_key';
  const GWP_REWARD_TYPE_PROPERTY = '_gwp_reward_type';

  let activePercent = BASE_PERCENT;
  let factor = (100 - activePercent) / 100;

  const ORIGINAL_ATTR = 'data-promo-original-cents';
  const COMPARE_CLASS = 'promo-price-compare';

  function parseMoneyToCents(str) {
    const n = parseFloat((str || '').replace(/[^0-9.]/g, ''));
    return isNaN(n) || n <= 0 ? 0 : Math.round(n * 100);
  }

  function formatMoney(cents) {
    const d = cents / 100;
    return '$' + (Math.round(d * 100) % 100 === 0 ? Math.round(d).toString() : d.toFixed(2));
  }

  // ── Generic patch for [data-variant-price] / [data-subscription-price] outside buy boxes ──

  function patchPriceEl(el) {
    if (!(el instanceof HTMLElement)) return;
    // Skip <s> tags — these are already-struck-through originals rendered by Liquid's promo block.
    // Re-patching them inserts a duplicate strikethrough and corrupts React-rendered bundle prices.
    if (el.tagName === 'S') return;
    if (
      el.closest('buy-box-form-v2') ||
      el.closest('[data-buy-box-v3-top-price]') ||
      el.closest('[data-buy-box-simplified-upsell]') ||
      el.closest('[data-buy-box-form-v2-data]') ||
      el.closest('core-react-product-form') ||
      // The PDP title/price header (product-details.liquid / product-details-v2.liquid)
      // mirrors whatever price core-react-product-form (or buy-box-form-v2/v3) just
      // rendered — which already has the promo baked in via Liquid. Re-patching here
      // would discount an already-discounted price a second time.
      el.closest('.js-product-details')
    ) return;

    const parent = el.parentElement;
    if (!parent) return;

    const storedAttr = el.getAttribute(ORIGINAL_ATTR);
    if (storedAttr) {
      const storedCents = Number(storedAttr);
      const expectedText = formatMoney(Math.round(storedCents * factor));
      if (el.textContent.trim() === expectedText) return;
      // Restore the ORIGINAL price before re-patching. Re-reading the current
      // (already discounted) text as the base would compound discounts when the
      // active tier changes.
      el.removeAttribute(ORIGINAL_ATTR);
      el.textContent = formatMoney(storedCents);
      const oldCompare = parent.querySelector('.' + COMPARE_CLASS);
      if (oldCompare) oldCompare.remove();
      const compareGroup = parent.querySelector('[data-compare-at-price-group]');
      if (compareGroup) compareGroup.style.display = '';
    }

    const raw = el.textContent.trim();
    const originalCents = parseMoneyToCents(raw);
    if (!originalCents) return;

    const discountedCents = Math.round(originalCents * factor);
    if (discountedCents === originalCents) return;

    // Keep the server-rendered compare-at (MSRP) strike untouched — the struck
    // price never changes when the promo turns on. Only synthesize a strike from
    // the pre-promo price when the element had no compare at all.
    const compareGroup = parent.querySelector('[data-compare-at-price-group]');

    el.setAttribute(ORIGINAL_ATTR, String(originalCents));
    el.textContent = formatMoney(discountedCents);

    if (!compareGroup) {
      const s = document.createElement('s');
      s.className = COMPARE_CLASS;
      s.textContent = raw;
      parent.insertBefore(s, el);
    }
  }

  // ── Patch for server-rendered product cards ([data-promo-base-cents]) ──
  // Homepage/collection card prices render in Liquid at the BASE promo percent;
  // the attribute carries the pre-promo cents so tier changes can re-price them
  // without re-rendering. Only trailing text nodes are touched — the sr-only
  // "Sale price:" span stays intact for screen readers.

  function patchCardPriceEl(el) {
    if (!(el instanceof HTMLElement)) return;
    const baseCents = Number(el.getAttribute('data-promo-base-cents') || 0);
    if (!baseCents) return;

    // Cards carry their own resolved percent (bundle cards = bundle percent) so a
    // repaint never flattens a mixed grid to the sitewide base percent. The cart
    // tier, when enabled, can only raise it.
    const elPct = Number(el.getAttribute('data-promo-base-percent') || 0);
    const effPct = Math.max(elPct, activePercent);
    const elFactor = effPct > 0 ? (100 - effPct) / 100 : factor;
    const discounted = Math.round(baseCents * elFactor);
    const text = formatMoney(discounted).replace('.00', '');

    let node = el.lastChild;
    while (node && node.nodeType !== Node.TEXT_NODE) node = node.previousSibling;
    if (node) {
      if (node.textContent.trim() !== text) node.textContent = text;
    } else {
      el.appendChild(document.createTextNode(text));
    }
  }

  function applyPromo() {
    document.querySelectorAll('[data-variant-price]').forEach(patchPriceEl);
    document.querySelectorAll('[data-subscription-price]').forEach(patchPriceEl);
    document.querySelectorAll('[data-promo-base-cents]').forEach(patchCardPriceEl);
  }

  // ── Patch OTP price inside the React buy box selling plan options ──
  // React reads variant.price (a Shopify object field) for the OTP option price —
  // that field can't be changed via Liquid, so we patch the DOM after React renders.

  function patchReactOtpOption(container) {
    // The OTP option contains input[value="onetime"]
    const otpInput = container.querySelector('input[name="purchase-type"][value="onetime"]');
    if (!otpInput) return;

    // Walk up to find the option wrapper
    let optionEl = otpInput.parentElement;
    while (optionEl && optionEl !== container) {
      if (optionEl.className && optionEl.className.includes('sellingPlanOptions__option')) break;
      optionEl = optionEl.parentElement;
    }
    if (!optionEl || optionEl === container) return;

    const priceStack = optionEl.querySelector('[class*="sellingPlanOptions__priceStack"]');
    if (!priceStack) return;

    const priceEl = priceStack.querySelector('[class*="sellingPlanOptions__pricePrimary"]');
    if (!priceEl) return;

    const storedAttr = priceStack.getAttribute(ORIGINAL_ATTR);
    if (storedAttr) {
      const storedCents = Number(storedAttr);
      const expectedText = formatMoney(Math.round(storedCents * factor));
      if (priceEl.textContent.trim() === expectedText) return;
      // Tier changed — restore the original before re-applying.
      priceStack.removeAttribute(ORIGINAL_ATTR);
      priceEl.textContent = formatMoney(storedCents);
      const oldCompare = priceStack.querySelector('.' + COMPARE_CLASS);
      if (oldCompare) oldCompare.remove();
    }

    const raw = priceEl.textContent.trim();
    const originalCents = parseMoneyToCents(raw);
    if (!originalCents) return;

    const discountedCents = Math.round(originalCents * factor);
    if (discountedCents === originalCents) return;

    priceStack.setAttribute(ORIGINAL_ATTR, String(originalCents));
    priceEl.textContent = formatMoney(discountedCents);

    const s = document.createElement('s');
    s.className = COMPARE_CLASS + ' ' + COMPARE_CLASS + '--otp';
    s.textContent = raw;
    priceStack.insertBefore(s, priceEl);
  }

  function patchAllReactOtpOptions() {
    document.querySelectorAll('core-react-product-form').forEach(patchReactOtpOption);
  }

  // ── Cart-aware tier resolution ──

  function eligibleQuantity(cart) {
    const items = Array.isArray(cart && cart.items) ? cart.items : [];
    return items.reduce(function (total, item) {
      const props = (item && item.properties) || {};
      const thresholdKey = String(props[GWP_THRESHOLD_KEY_PROPERTY] || '');
      const rewardType = String(props[GWP_REWARD_TYPE_PROPERTY] || '').toLowerCase();
      if (thresholdKey !== '' || rewardType === 'gift') return total;
      return total + Number((item && item.quantity) || 0);
    }, 0);
  }

  function setActivePercent(percent) {
    if (percent === activePercent) return;
    activePercent = percent;
    factor = (100 - activePercent) / 100;
    applyPromo();
    patchAllReactOtpOptions();
    document.dispatchEvent(new CustomEvent('promo:tier-change', {
      detail: { percent: activePercent, factor: factor }
    }));
  }

  function resolveTierFromCart(cart) {
    if (TIER2_PERCENT <= 0) return;
    const qty = eligibleQuantity(cart);
    setActivePercent(qty >= TIER2_MIN_ITEMS ? TIER2_PERCENT : BASE_PERCENT);
  }

  let fetchTimer;
  function refreshTierFromCartApi() {
    if (TIER2_PERCENT <= 0) return;
    clearTimeout(fetchTimer);
    fetchTimer = setTimeout(function () {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (cart) { if (cart) resolveTierFromCart(cart); })
        .catch(function () { /* keep current tier on network errors */ });
    }, 150);
  }

  // ── Init ──

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyPromo);
  } else {
    applyPromo();
  }

  // Watch for React's initial mount (buy box renders after page load)
  let debounceTimer;
  new MutationObserver(function () {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      patchAllReactOtpOptions();
      applyPromo();
    }, 50);
  }).observe(document.body, { childList: true, subtree: true });

  patchAllReactOtpOptions();

  if (TIER2_PERCENT > 0) {
    // The drawer dispatches cart:updated after every mutation (add/remove/qty);
    // product forms dispatch cart:add. Either way, re-resolve from /cart.js so the
    // tier always reflects the server-side cart state.
    document.addEventListener('cart:add', refreshTierFromCartApi);
    document.addEventListener('cart:updated', refreshTierFromCartApi);
    refreshTierFromCartApi();
  }
})();
